import type { TypologyTakeoff } from '@obraplan/core/domain/takeoff/consolidation';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  comodosDaTipologia,
  fundacaoDaTipologia,
  pileDaTipologia,
  quantitiesOf,
} from './resumo-dos-itens';
import {
  floorsOf,
  quantitiesOfUnit,
  shadowOf,
  unitsOf,
  type TypologyDraft,
  type UnitDraft,
  type WorkspaceDraft,
} from './typology-draft';

/**
 * A obra somada **apto a apto**, em parcelas — a base da consolidação "Obra inteira" e do
 * orçamento da obra (Fase 29, etapa 4).
 *
 * Vive fora da tela porque duas leituras precisam da **mesma** conta: a Consolidação, que a
 * mostra por material, e o Orçamento, que a mostra por disciplina e serviço com o preço. Com
 * duas contas, o número que vira pedido de compra e o que vira orçamento discordariam na
 * primeira mudança de uma delas.
 */

/** O medido de cada serviço numa tipologia, pela chave do serviço (Fase 24, fatia 6). */
export type MedidosDe = (typology: TypologyDraft) => Readonly<Record<string, number>>;

/**
 * Uma parcela da soma da obra — e o que dizer dela na tela.
 *
 * Carrega os campos que `consolidateProject` precisa (`name`, `floors`, `quantities`) e
 * mais a chave e o papel dela, para a lista de contribuições e o total falarem do **mesmo**
 * número. Eram duas contas separadas, e elas discordavam: a lista dizia "× 12 pavimentos"
 * enquanto a soma usava dez mais dois aptos à parte.
 */
export interface Parcela extends TypologyTakeoff {
  readonly chave: string;
  /** Um apto que diverge da tipologia, e não o grupo que a segue. */
  readonly divergente: boolean;
}

/**
 * A obra somada **apto a apto** (RN-167).
 *
 * Os que seguem a tipologia entram agrupados — é a razão de o quantitativo morar nela:
 * cento e vinte aptos iguais são uma linha vezes cento e vinte, e não cento e vinte
 * contas. Só o que **diverge** ganha entrada própria, com as quantidades dele.
 *
 * Sem esta separação, a divergência do 1204 sumia da soma: a conta era "tipologia vezes
 * andares", e a tipologia não sabe do 1204.
 */
export function aptosDaObra(
  workspace: WorkspaceDraft,
  recorteDe?: (typology: TypologyDraft) => ServiceCatalog,
  medidosDe?: MedidosDe,
): readonly Parcela[] {
  const divergentesPorTipologia = new Map<string, UnitDraft[]>();
  for (const unit of unitsOf(workspace)) {
    if (shadowOf(workspace, unit.key) === undefined) continue;
    const id = unit.typology.unitTypeId;
    const lista = divergentesPorTipologia.get(id);
    if (lista === undefined) divergentesPorTipologia.set(id, [unit]);
    else lista.push(unit);
  }

  return workspace.typologies.flatMap((typology): readonly Parcela[] => {
    const total = floorsOf(workspace, typology);
    /*
      Quem manda é `floorsOf`, e os aptos divergentes cabem dentro dele.

      Os dois números vêm de fontes diferentes: `floorsOf` pode sair de um número próprio
      guardado na tipologia, e `unitsOf` sai sempre da estrutura. Quando discordam — uma
      tipologia com `floors: 1` numa torre de doze pavimentos tipo —, subtrair um do
      outro dava total negativo, o grupo que segue era descartado pelo `> 0`, e a
      tipologia contribuía só pelos divergentes: mais vezes do que ela acontece.
    */
    const divergentes = (divergentesPorTipologia.get(typology.unitTypeId) ?? []).slice(0, total);
    const seguem = total - divergentes.length;

    return [
      ...(seguem > 0
        ? [
            {
              chave: typology.unitTypeId,
              divergente: false,
              name: typology.name,
              floors: seguem,
              quantities: quantitiesOf(
                typology,
                medidosDe?.(typology),
                undefined,
                pileDaTipologia(typology, workspace.pileTypes),
                comodosDaTipologia(workspace, typology),
                fundacaoDaTipologia(workspace, typology),
              ),
              ...(recorteDe === undefined ? {} : { catalog: recorteDe(typology) }),
            },
          ]
        : []),
      ...divergentes.map((unit): Parcela => ({
        chave: unit.key,
        divergente: true,
        name: `${unit.floor.name} · ${unit.typology.name}`,
        floors: 1,
        // O apto que diverge tem o desenho da tipologia dele: o medido é o mesmo, e o
        // calculado vale onde o apto não digitou por cima.
        quantities: {
          ...quantitiesOf(
            unit.typology,
            medidosDe?.(unit.typology),
            undefined,
            pileDaTipologia(unit.typology, workspace.pileTypes),
            comodosDaTipologia(workspace, unit.typology),
            fundacaoDaTipologia(workspace, unit.typology),
          ),
          overrides: quantitiesOfUnit(workspace, unit),
        },
        ...(recorteDe === undefined ? {} : { catalog: recorteDe(unit.typology) }),
      })),
    ];
  });
}
